"use client"

import * as React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@oxpa/shared/ui/button"
import { saveLeadCapture } from "@/app/actions/lead-actions"
import { CheckCircle2, Loader2, Download } from "lucide-react"

const leadFormSchema = z.object({
  name: z.string().min(2, "Имя должно содержать минимум 2 символа"),
  contact: z.string().min(5, "Укажите email, телефон или Telegram"),
})

export type LeadFormValues = z.infer<typeof leadFormSchema>

interface LeadMagnetFormProps {
  source: string
  buttonText: string
  onSuccess?: () => void
}

export function LeadMagnetForm({ source, buttonText, onSuccess }: LeadMagnetFormProps) {
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [isSuccess, setIsSuccess] = React.useState(false)
  const [submitError, setSubmitError] = React.useState<string | null>(null)
  const [consent, setConsent] = React.useState(false)

  const form = useForm<LeadFormValues>({
    resolver: zodResolver(leadFormSchema),
    defaultValues: {
      name: "",
      contact: "",
    },
  })

  async function onSubmit(data: LeadFormValues) {
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // 152-ФЗ: записываем согласие перед отправкой формы
      fetch("/api/pdpn/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source: "lead_magnet", metadata: { name: data.name, contact: data.contact } }),
      }).catch(() => { /* silent */ })

      const result = await saveLeadCapture(data.name, data.contact, source)
      
      if (result.success) {
        setIsSuccess(true)
        if (onSuccess) {
          // Delaying onSuccess call slightly so user can read the success message
          setTimeout(() => onSuccess(), 1500)
        }
      } else {
        setSubmitError(result.error || "Произошла ошибка при отправке")
      }
    } catch (err) {
      setSubmitError("Неизвестная ошибка")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-center space-y-4 animate-in fade-in duration-500">
        <CheckCircle2 className="w-16 h-16 text-[#4A90E2] animate-bounce" />
        <div>
          <h4 className="font-bold text-xl text-white">Спасибо!</h4>
          <p className="text-white/60 text-sm mt-2 max-w-[250px] mx-auto leading-relaxed">
            Ваши данные получены. Материал сейчас начнет скачиваться (здесь будет триггер логики загрузки).
          </p>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <input 
          {...form.register("name")}
          type="text" 
          placeholder="Ваше имя" 
          disabled={isSubmitting}
          className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#4A90E2] transition-colors"
        />
        {form.formState.errors.name && (
          <p className="text-red-400 text-[11px] px-1">{form.formState.errors.name.message}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <input 
          {...form.register("contact")}
          type="text" 
          placeholder="Эл. почта, телефон или ник Telegram" 
          disabled={isSubmitting}
          className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#4A90E2] transition-colors"
        />
        {form.formState.errors.contact && (
          <p className="text-red-400 text-[11px] px-1">{form.formState.errors.contact.message}</p>
        )}
      </div>

      {submitError && (
        <p className="text-red-400 text-sm text-center bg-red-400/10 p-2 rounded-md border border-red-400/20">{submitError}</p>
      )}

      {/* 152-ФЗ: чекбокс явного согласия на обработку ПДн */}
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          disabled={isSubmitting}
          className="mt-0.5 w-4 h-4 shrink-0 rounded border-white/20 bg-white/5 text-[#4A90E2] 
            focus:ring-[#4A90E2] focus:ring-offset-0 focus:ring-1 
            disabled:opacity-50 cursor-pointer"
        />
        <span className="text-[10px] text-white/40 leading-relaxed group-hover:text-white/60 transition-colors">
          Я даю согласие на обработку персональных данных в соответствии с{" "}
          <a href="/privacy" target="_blank" className="underline text-white/50 hover:text-white/80 transition-colors">
            Политикой конфиденциальности
          </a>
        </span>
      </label>

      <Button 
        type="submit"
        disabled={!consent || isSubmitting}
        className="w-full h-12 bg-[#4A90E2] hover:bg-[#357ABD] text-white transition-colors flex items-center justify-center gap-2 font-medium disabled:opacity-50"
      >
        {isSubmitting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Download className="w-5 h-5" />
            {buttonText}
          </>
        )}
      </Button>
    </form>
  )
}
